/**
 * Login Form - Mad green/black theme.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast, useI18n } from '../../providers';
import { getPhoneValidation, formatPhoneInput, getFullPhoneNumber, validateCameroonPhone } from '../../utils/phoneValidation';

const LoginForm = ({ onSuccess, transactionId }) => {
  const navigate = useNavigate();
  const { loginWithPhone, loading } = useAuth();
  const { showError, showSuccess } = useToast();
  const { language } = useI18n();

  const [formData, setFormData] = useState({ countryCode: '+237', phone: '' });
  const [errors, setErrors] = useState({});
  const [phoneValidation, setPhoneValidation] = useState(getPhoneValidation('+237'));

  const countryCodes = [
    { code: '+237', country: 'Cameroon', flag: '🇨🇲', name: language === 'fr' ? 'Cameroun' : 'Cameroon' },
    { code: '+254', country: 'Kenya', flag: '🇰🇪', name: 'Kenya' },
    { code: '+256', country: 'Uganda', flag: '🇺🇬', name: 'Uganda' },
    { code: '+255', country: 'Tanzania', flag: '🇹🇿', name: 'Tanzania' },
    { code: '+250', country: 'Rwanda', flag: '🇷🇼', name: 'Rwanda' },
    { code: '+234', country: 'Nigeria', flag: '🇳🇬', name: 'Nigeria' },
    { code: '+233', country: 'Ghana', flag: '🇬🇭', name: 'Ghana' },
  ];

  useEffect(() => {
    setPhoneValidation(getPhoneValidation(formData.countryCode));
  }, [formData.countryCode]);

  const handleChange = (field, value) => {
    if (field === 'phone') {
      setFormData((prev) => ({ ...prev, [field]: formatPhoneInput(value, formData.countryCode) }));
    } else if (field === 'countryCode') {
      setFormData((prev) => ({ ...prev, [field]: value, phone: '' }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};
    const cleanedPhone = formData.phone.replace(/\s/g, '');
    if (!cleanedPhone) {
      newErrors.phone = language === 'fr' ? 'Le numéro de téléphone est requis' : 'Phone number is required';
    } else if (formData.countryCode === '+237') {
      const validation = validateCameroonPhone(cleanedPhone);
      if (!validation.valid) newErrors.phone = validation.error;
    } else {
      const validation = getPhoneValidation(formData.countryCode);
      if (!validation.pattern.test(cleanedPhone) || cleanedPhone.length !== validation.length) {
        newErrors.phone = validation.error;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      const phoneNumber = getFullPhoneNumber(formData.phone, formData.countryCode);
      await loginWithPhone({ phone: phoneNumber });
      showSuccess(language === 'fr' ? 'Code de vérification envoyé!' : 'Verification code sent!');
      if (onSuccess) onSuccess(phoneNumber);
      else navigate('/auth/verify-otp', { state: { phone: phoneNumber, transactionId, isLogin: true } });
    } catch (error) {
      const errorMessage = error?.message || error?.errors?.[0] || (language === 'fr' ? 'Échec de l\'envoi du code' : 'Failed to send verification code');
      showError(errorMessage);
    }
  };

  return (
    <div className="auth-mad">
      <div className="auth-mad-bg" aria-hidden="true" />
      <div className="auth-mad-grid" aria-hidden="true" />
      <div className="auth-mad-inner">
        <button type="button" onClick={() => navigate(-1)} className="auth-mad-back">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 19l-7-7 7-7" />
          </svg>
          <span>{language === 'fr' ? 'Retour' : 'Back'}</span>
        </button>

        <div className="auth-mad-card">
          <div className="auth-mad-header">
            <div className="auth-mad-icon" aria-hidden>🔐</div>
            <h1 className="auth-mad-title">{language === 'fr' ? 'Connexion' : 'Login'}</h1>
            <p className="auth-mad-sub">
              {language === 'fr' ? 'Entrez votre numéro (indicatif déjà affiché)' : 'Enter your number (country code already shown)'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="auth-mad-form">
            <div className="auth-mad-field">
              <label className="auth-mad-label">{language === 'fr' ? 'Votre numéro' : 'Your number'}</label>
              <div className="auth-mad-phone-row">
                <div className="auth-mad-select-wrap">
                  <select
                    value={formData.countryCode}
                    onChange={(e) => handleChange('countryCode', e.target.value)}
                    className="auth-mad-select"
                    aria-label="Country code"
                  >
                    {countryCodes.map((c) => (
                      <option key={c.code} value={c.code}>{c.flag} {c.code}</option>
                    ))}
                  </select>
                </div>
                <div className="auth-mad-input-wrap">
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder={phoneValidation.placeholder}
                    required
                    className={errors.phone ? 'border-red-500' : ''}
                    inputMode="numeric"
                    aria-invalid={!!errors.phone}
                  />
                  {errors.phone && <p className="auth-mad-error">{errors.phone}</p>}
                  {formData.phone && !errors.phone && (
                    <p className="auth-mad-hint">{language === 'fr' ? 'Exemple' : 'Example'}: {phoneValidation.example}</p>
                  )}
                </div>
              </div>
            </div>

            <button type="submit" className="auth-mad-btn" disabled={loading}>
              {loading ? (language === 'fr' ? 'Envoi…' : 'Sending…') : (language === 'fr' ? 'Envoyer le code →' : 'Send Code →')}
            </button>

            <div className="auth-mad-link-wrap">
              <button type="button" onClick={() => navigate('/auth/forgot-password')} className="auth-mad-link">
                {language === 'fr' ? 'Mot de passe oublié?' : 'Forgot Password?'}
              </button>
            </div>

            <div className="auth-mad-divider" />

            <div className="auth-mad-footer">
              <p>
                {language === 'fr' ? 'Pas de compte?' : "Don't have an account?"}
                <button type="button" onClick={() => navigate('/auth/signup', { state: { transactionId } })} className="auth-mad-link">
                  {language === 'fr' ? 'S\'inscrire' : 'Sign Up'}
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
